import React from 'react'
import { Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'
import {
  Info,
  Screenshot,
  Step,
  Success,
  Warning,
} from '../../components/DocsComponents'

const ManagingEmployees = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">The Employees module</Typography>
      <Typography>
        The Employees module supports the whole employee lifecycle — from
        recruitment and onboarding through active deployment, training and
        compliance management, appraisals, and eventually offboarding.
      </Typography>
      <Info title="Who can manage employees?">
        Employee records are created, edited, and archived by users in the{' '}
        <strong>Personnel</strong> team. Site administrators always have
        access. The People section of the sidebar is only shown to these
        users.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">What's in an employee record</Typography>
      <Typography>
        Each employee has a centralised profile organised into tabs. On a
        desktop each tab shows its name; on a phone the tabs collapse to icons
        only — tap and hold (or hover) an icon to see its name, and each
        section opens with its own heading:
      </Typography>
      <ul style={{ marginTop: 0 }}>
        <li>
          <strong>Details</strong> — personal information, contact details,
          address, next of kin, driving licence details, DBS information,
          controlled drugs information, teams and permissions, and clinical
          role.
        </li>
        <li>
          <strong>Mandatory Training</strong> — training records against your
          organisation's required courses, with expiry monitoring. Personnel
          can add a record for any course (with an optional certificate), edit
          the date, notes or certificate of a hand-entered record, and approve
          or reject certificates staff upload themselves. e-Learning results
          are read-only.
        </li>
        <li>
          <strong>Qualifications</strong> — professional qualifications and
          certificates.
        </li>
        <li>
          <strong>Payroll &amp; Shifts</strong> — employment type, contracted
          hours, BACS details, annual leave settings, and the employee's shift
          history.
        </li>
        <li>
          <strong>Documents</strong> — files held against the employee.
        </li>
        <li>
          <strong>Notes</strong> — free-text personnel notes.
        </li>
        <li>
          <strong>Appraisals</strong> — appraisal history and reviews (see{' '}
          <DocLink text="Appraisals" url="/employees-hr/appraisals" docsPage />
          ).
        </li>
        <li>
          <strong>Occupational Health</strong> — screening status, submission
          history, and requesting a new screening (see{' '}
          <DocLink
            text="Occupational Health"
            url="/employees-hr/occupational-health"
            docsPage
          />
          ).
        </li>
        <li>
          <strong>Radiation Doses</strong> — shown only for organisations with
          the imaging module enabled: the employee's recorded radiation dose
          history from imaging requests.
        </li>
      </ul>
      <Screenshot url="employees/employee-profile.png">
        An employee record with its profile header and the tabbed sections
        beneath.
      </Screenshot>
      <Typography>
        The profile header also shows the employee's status —{' '}
        <strong>New Starter</strong>, <strong>Live</strong>,{' '}
        <strong>Suspended</strong>, or <strong>Leaver</strong> — alongside
        chips for toggling login access and task assignment.
      </Typography>
      <Typography>
        New starters created from an accepted application also carry an{' '}
        <strong>Onboarding Checklist</strong> card on their profile, listing
        your organisation's new-starter tasks (issue uniform, complete
        induction, and so on) to tick off as they are completed.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Recruitment and onboarding</Typography>
      <Typography>
        AmbuNet supports digital recruitment workflows, allowing applicants to
        submit applications online, upload qualifications and documents,
        provide references, complete onboarding tasks, verify identity
        information, and accept organisational policies. Once approved,
        applicants transition into employee records from{' '}
        <DocLink text="Applications" url="/employees/applications" />. For your
        shareable application link and a step-by-step guide, see{' '}
        <DocLink
          docsPage
          url="/employees-hr/recruitment-applications"
          text="Recruitment & Applications"
        />
        .
      </Typography>
      <Info>
        Employee applications require a Premium (tier 2) or higher
        subscription.
      </Info>
      <Success title="Paperless onboarding">
        AmbuNet reduces manual administration by centralising recruitment and
        onboarding into a single workflow.
      </Success>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Manage Access</Typography>
      <Typography>
        <DocLink
          text="Site Config > Employees > Manage Access"
          url="/site-configuration/manage-access"
        />{' '}
        is the main place to set access in bulk: one matrix of every active
        employee where Personnel assign teams, the PTS Co-Ordinator flag, and
        Command &amp; Control levels without opening each record. You cannot
        change your own access from the matrix — that needs a Site Admin. Site
        admins are pinned to the top with every permission shown as granted.
      </Typography>
      <Screenshot url="employees/manage-access.png">
        Manage Access shows every staff member's teams, PTS Co-Ordinator and
        Control Room access in one grid.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Training and compliance</Typography>
      <Typography>
        The Employees module integrates with AmbuNet's compliance systems to
        help you monitor workforce readiness. This includes mandatory training
        expiry monitoring, qualification tracking, DBS renewal monitoring and
        the <DocLink text="DBS Checks" url="/dbs-checks" /> dashboard, driving
        licence checks, and staff ID cards. For the DBS Update Service
        monitoring, driving licence records, and ID cards in detail, see{' '}
        <DocLink
          docsPage
          url="/employees-hr/dbs-driving-licence-id"
          text="DBS Checks, Driving Licences & ID"
        />
        .
      </Typography>
      <Typography>
        The <DocLink
          text="Workforce Dashboard"
          url="/employees-hr/personnel-dashboard"
          docsPage
        />{' '}
        collects everything needing attention — expired training, out-of-date
        DBS checks, overdue licence checks, missing ID cards, and pending
        reviews — into a single Action Centre.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">
        Shift eligibility and operational readiness
      </Typography>
      <Typography>
        Employee records feed scheduling and operational modules across
        AmbuNet. Assignment checks use the record to warn about role
        mismatches, inactive employment status, training issues, and DBS
        issues before a shift is saved, and the clinical role controls which
        advertised shifts a staff member can see and apply for.
      </Typography>
      <Warning title="Operational safety">
        Keep employee records accurate and up to date to support safe staffing
        decisions.
      </Warning>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">
        Payroll profiles and leave settings
      </Typography>
      <Typography>
        Personnel users manage payroll profile information from the employee
        record's Payroll tab: employment type, contracted hours per week,
        annual leave entitlement, leave year start month, working hours per
        day for leave calculations, and BACS details.
      </Typography>
      <Typography>
        Employment type is used by pay runs to group PAYE-style workers
        separately from invoice/remittance workers. Annual leave entitlement
        and working-hours settings drive the holiday balance shown to staff on
        My Availability.
      </Typography>
      <Info>
        AmbuNet payroll profiles support operational pay-run control and staff
        visibility. They do not calculate statutory deductions, pension
        contributions, or HMRC submissions.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Subcontractors</Typography>
      <Typography>
        External clinicians who work your shifts without being part of your
        workforce are managed separately from employees at{' '}
        <DocLink text="Employees > Subcontractors" url="/employees/subcontractors" />
        , with time-boxed access windows and a restricted clinical-tools
        view. See{' '}
        <DocLink
          docsPage
          url="/employees-hr/subcontractors"
          text="Subcontractors"
        />
        for creating and managing their accounts, and{' '}
        <DocLink
          docsPage
          url="/employees-hr/working-with-subcontractors"
          text="Working with Subcontractors"
        />
        for shift assignment and what they can do.
      </Typography>
      <Info title="Who can do this?">
        Managing subcontractors requires the <strong>Personnel</strong> team.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Removing employees</Typography>
      <Typography>
        When an employee leaves your organisation, mark them as a leaver
        instead of deleting the record. This preserves audit history, shift
        records, incident involvement, training history, and patient care
        associations. See{' '}
        <DocLink
          text="Removing Employees"
          url="/employees-hr/removing-employees"
          docsPage
        />
        .
      </Typography>
    </Stack>

    <Stack gap={0}>
      <Typography variant="title3">Best practices</Typography>
      <Step number={1} title="Keep records updated">
        Ensure employee contact details, qualifications, and compliance
        records are regularly reviewed.
      </Step>
      <Step number={2} title="Use onboarding workflows">
        Digital onboarding helps standardise recruitment and reduces missing
        information.
      </Step>
      <Step number={3} title="Monitor compliance">
        Review the Workforce Dashboard's Action Centre regularly for training,
        DBS, licence, and appraisal actions.
      </Step>
      <Step number={4} title="Avoid deleting records">
        Use the leaver workflow instead of deleting records to preserve audit
        history.
      </Step>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Related documentation</Typography>
      <Typography>
        <DocLink
          text="Adding Employees"
          url="/employees-hr/adding-employees"
          docsPage
        />
        <DocLink
          text="Removing Employees"
          url="/employees-hr/removing-employees"
          docsPage
        />
        <DocLink
          text="Workforce Dashboard"
          url="/employees-hr/personnel-dashboard"
          docsPage
        />
        <DocLink text="Appraisals" url="/employees-hr/appraisals" docsPage />
      </Typography>
    </Stack>
  </Stack>
)

export default ManagingEmployees
